import {ReactNode, useEffect, useState} from 'react';
import {requestContext} from '../../../../../../common/requestContext';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import {Loading} from '../../../../../../design-system/atoms/Loading/Loading';
import {ViewerGuideType} from '../../../../../../design-system/organisms/ViewerGuide/ViewerGuideType.enum';
import {ViewerSettingFontType} from '../../../../../../design-system/organisms/ViewerSetting/ViewerSettingFontItemType.interface';
import {ViewerSettingSpaceColorType} from '../../../../../../design-system/organisms/ViewerSetting/ViewerSettingSpaceColorItemType.interface';
import {ViewerSettingThemeType} from '../../../../../../design-system/organisms/ViewerSetting/ViewerSettingThemeItemType.interface';
import {
  ImageViewerData,
  ReadType,
  SlideType,
  TalkViewerData,
  TalkViewerType,
  ViewDirection,
} from '../../../../../../graphql/generated/types';
import {ONLY_DATA_CONFIG} from '../../../../../../reactQuery/constant';
import {useViewerInfoQuery} from '../../../../../../wrapper/viewerWrapper';
import {
  IS_TWO_PAGE_LAYOUT_DEFAULT,
  LOCAL_STORAGE_VIEWER_IS_PREVIEW_COMMENT_ON,
  LOCAL_STORAGE_VIEWER_SETTING_CONTENT_MARGIN_KEY,
  LOCAL_STORAGE_VIEWER_SETTING_FONT_FAMILY_KEY,
  LOCAL_STORAGE_VIEWER_SETTING_FONT_SIZE_KEY,
  LOCAL_STORAGE_VIEWER_SETTING_IS_TWO_PAGE_LAYOUT,
  LOCAL_STORAGE_VIEWER_SETTING_LINE_GAP_KEY,
  LOCAL_STORAGE_VIEWER_SETTING_READ_TYPE_KEY,
  LOCAL_STORAGE_VIEWER_SETTING_SPACE_COLOR_KEY,
  LOCAL_STORAGE_VIEWER_SETTING_THEME_KEY,
  LOCAL_STORAGE_VIEWER_TOOLTIP_IS_CLOSED,
  ViewerType,
} from '../constant/viewerConstant';
import {
  changeSpaceColor,
  changeReadType as imageViewerChangeReadType,
  changeTwoPageLayout as imageViewerChangeTwoPageLayout,
} from '../state/imageViewerSlice';
import {changeViewingMode} from '../state/talkViewerSlice';
import {
  changeContentMargin,
  changeFontFamily,
  changeFontSize,
  changeLineGap,
  changeTheme,
  changeReadType as textViewerChangeReadType,
  changeTwoPageLayout as textViewerChangeTwoPageLayout,
} from '../state/textViewerSlice';
import {
  resetViewerGuide,
  setCurrentViewerGuide,
  setIsViewerPreviewCommentOn,
  setIsViewerToolTipExposed,
} from '../state/viewerInfoSlice';
import {ImageViewerReadType} from '../type/ImageViewerReadType';
import {slideTypeToViewerType} from '../type/slideTypeToViewerType';
import {TextViewerReadType} from '../type/TextViewerReadType';
import {ViewingMode} from '../type/ViewingMode.enum';
import {convertViewerReadTypeToGuideType} from '../util/convertViewerReadTypeToGuideType';
import {getViewerSettingFromLocalStorage} from '../util/getViewerSettingFromLocalStorage';

export interface ViewerSettingLocalStorageFetcherHeadlessProps {
  seriesId: number;
  productId: number;
  isPreview?: boolean;
  children: ReactNode;
}

export function ViewerSettingLocalStorageFetcherHeadless({
  seriesId,
  productId,
  isPreview,
  children,
}: ViewerSettingLocalStorageFetcherHeadlessProps) {
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  const {data, isSuccess} = useViewerInfoQuery(
    {seriesId, productId},
    ONLY_DATA_CONFIG
  );
  const viewerInfoData = data;

  const slideType = viewerInfoData?.item?.slideType;
  const viewerType = slideTypeToViewerType(slideType || SlideType.None);

  const isTapOnlyTalkViewer =
    (viewerInfoData?.viewerData as TalkViewerData)?.talkDownloadData
      ?.talkViewerType === TalkViewerType.Tap;

  // XXX : 이미지 뷰어에서는 state.imageViewer로 옮겨 사용하는 값이지만 fetcher가 불리기 전이라 viewerData에서 가져옴. 개선 필요
  const isScrollOnlyImageViewer =
    (viewerInfoData?.viewerData as ImageViewerData)?.imageDownloadData
      ?.readType === ReadType.Scroll; // 스크롤만 가능한 회차의 경우 서버에서 주는 ReadType이 Scroll

  const isReverseViewDirection =
    (viewerInfoData?.viewerData as ImageViewerData)?.imageDownloadData
      ?.viewDirection === ViewDirection.Backward;

  const dispatch = useTypedDispatch();

  const {isMobile, isWebView} = requestContext.requestInfo.display;

  useEffect(() => {
    if (!isSuccess || isLoaded || !data) return;
    //공통 케이스
    const viewerSettingReadType = getViewerSettingFromLocalStorage(
      viewerType,
      LOCAL_STORAGE_VIEWER_SETTING_READ_TYPE_KEY
    );
    if (viewerSettingReadType) {
      switch (viewerType) {
        case ViewerType.TextViewer: {
          const readType = isPreview
            ? TextViewerReadType.Scroll
            : (viewerSettingReadType as TextViewerReadType);
          dispatch(textViewerChangeReadType(readType));
          dispatch(
            setCurrentViewerGuide(
              convertViewerReadTypeToGuideType({
                viewerType: ViewerType.TextViewer,
                viewerReadType: readType,
              })
            )
          );
          break;
        }
        case ViewerType.ImageViewer:
          // 스크롤만 가능한 회차의 경우 로컬스토리지 정보를 무시하고 스크롤로 설정
          if (isScrollOnlyImageViewer) {
            dispatch(imageViewerChangeReadType(ImageViewerReadType.Scroll));
            dispatch(setCurrentViewerGuide(ViewerGuideType.Scroll));
            break;
          }

          dispatch(
            imageViewerChangeReadType(
              isPreview
                ? ImageViewerReadType.Scroll
                : (viewerSettingReadType as ImageViewerReadType)
            )
          );
          dispatch(
            setCurrentViewerGuide(
              convertViewerReadTypeToGuideType({
                viewerType: ViewerType.ImageViewer,
                viewerReadType: viewerSettingReadType as ImageViewerReadType,
                isReverseViewDirection,
              })
            )
          );
          break;
        case ViewerType.TalkViewer:
          // 탭만 가능한 회차의 경우 로컬스토리지 정보를 무시하고 탭으로 설정
          if (isTapOnlyTalkViewer) {
            dispatch(changeViewingMode(ViewingMode.Tap));
            dispatch(setCurrentViewerGuide(ViewerGuideType.Tap));
            break;
          }
          dispatch(
            changeViewingMode(
              (viewerSettingReadType as ViewingMode) === ViewingMode.Tap
                ? ViewingMode.Tap
                : ViewingMode.Scroll
            )
          );
          dispatch(
            setCurrentViewerGuide(
              convertViewerReadTypeToGuideType({
                viewerType: ViewerType.TalkViewer,
                viewerReadType: viewerSettingReadType as ViewingMode,
              })
            )
          );
          break;
        default:
          dispatch(resetViewerGuide());
          break;
      }
    } else {
      // 로컬 스토리지 없을 경우 뷰어 가이드 기본 값 설정
      switch (viewerType) {
        case ViewerType.TextViewer:
          dispatch(setCurrentViewerGuide(ViewerGuideType.Forward));
          break;
        case ViewerType.ImageViewer:
          if (isScrollOnlyImageViewer) {
            dispatch(imageViewerChangeReadType(ImageViewerReadType.Scroll));
            dispatch(setCurrentViewerGuide(ViewerGuideType.Scroll));
            break;
          }
          dispatch(imageViewerChangeReadType(ImageViewerReadType.Page));
          dispatch(
            setCurrentViewerGuide(
              isReverseViewDirection
                ? ViewerGuideType.Reverse
                : ViewerGuideType.Forward
            )
          );
          break;
        case ViewerType.TalkViewer:
          if (isTapOnlyTalkViewer) {
            dispatch(changeViewingMode(ViewingMode.Tap));
            dispatch(setCurrentViewerGuide(ViewerGuideType.Tap));
            break;
          }
          dispatch(setCurrentViewerGuide(ViewerGuideType.Tap));
          break;
        default:
          dispatch(resetViewerGuide());
          break;
      }
    }
    const viewerSettingIsTwoPageLayout = getViewerSettingFromLocalStorage(
      viewerType,
      LOCAL_STORAGE_VIEWER_SETTING_IS_TWO_PAGE_LAYOUT
    );
    if (viewerSettingIsTwoPageLayout) {
      switch (viewerType) {
        case ViewerType.TextViewer:
          // 실제로는 모바일에서는 이 값을 설정할 방법이 없기 때문에 상관없으나
          // 개발중에는 모바일/PC 환경을 오가는 경우가 많으므로 로컬스토리지에 저장된 값이 영향을 주지 못하도록
          // 값이 로드될 때 스토어에 false로 넣어둡니다.
          dispatch(
            textViewerChangeTwoPageLayout(
              isMobile ? false : JSON.parse(viewerSettingIsTwoPageLayout)
            )
          );
          break;
        case ViewerType.ImageViewer:
          dispatch(
            imageViewerChangeTwoPageLayout(
              isMobile || isScrollOnlyImageViewer
                ? false
                : JSON.parse(viewerSettingIsTwoPageLayout)
            )
          );
          break;
        default:
          break;
      }
    } else {
      switch (viewerType) {
        case ViewerType.TextViewer:
          // 이 값은 두쪽으로 보여지는 여부 뿐만 아니라 페이지 이동할 때도 영향을 미치는 값이라
          // 모바일 상태라면 항상 false를 유지해야합니다.
          // 따라서, 로컬스토리지에 저장된 값이 없을 경우도 두쪽보기 기본값을 상황에 맞게 설정해둡니다.
          dispatch(
            textViewerChangeTwoPageLayout(
              isMobile ? false : IS_TWO_PAGE_LAYOUT_DEFAULT
            )
          );
          break;
        case ViewerType.ImageViewer:
          dispatch(
            imageViewerChangeTwoPageLayout(
              isMobile ? false : IS_TWO_PAGE_LAYOUT_DEFAULT
            )
          );
          break;
        default:
          break;
      }
    }
    //TextViewer 케이스
    const viewerSettingTheme = getViewerSettingFromLocalStorage(
      viewerType,
      LOCAL_STORAGE_VIEWER_SETTING_THEME_KEY
    );
    if (viewerSettingTheme) {
      dispatch(changeTheme(viewerSettingTheme as ViewerSettingThemeType));
    }
    const viewerSettingFontFamily = getViewerSettingFromLocalStorage(
      viewerType,
      LOCAL_STORAGE_VIEWER_SETTING_FONT_FAMILY_KEY
    );
    if (viewerSettingFontFamily) {
      dispatch(
        changeFontFamily(viewerSettingFontFamily as ViewerSettingFontType)
      );
    }
    const viewerSettingFontSize = getViewerSettingFromLocalStorage(
      viewerType,
      LOCAL_STORAGE_VIEWER_SETTING_FONT_SIZE_KEY
    );
    if (viewerSettingFontSize) {
      dispatch(changeFontSize(parseInt(viewerSettingFontSize)));
    }
    const viewerSettingLineGap = getViewerSettingFromLocalStorage(
      viewerType,
      LOCAL_STORAGE_VIEWER_SETTING_LINE_GAP_KEY
    );
    if (viewerSettingLineGap) {
      dispatch(changeLineGap(parseInt(viewerSettingLineGap)));
    }
    const viewerSettingContentMargin = getViewerSettingFromLocalStorage(
      viewerType,
      LOCAL_STORAGE_VIEWER_SETTING_CONTENT_MARGIN_KEY
    );
    if (viewerSettingContentMargin) {
      dispatch(changeContentMargin(parseInt(viewerSettingContentMargin)));
    }
    //ImageViewer 케이스
    const viewerSettingSpaceColor = getViewerSettingFromLocalStorage(
      viewerType,
      LOCAL_STORAGE_VIEWER_SETTING_SPACE_COLOR_KEY
    );
    if (viewerSettingSpaceColor) {
      dispatch(
        changeSpaceColor(viewerSettingSpaceColor as ViewerSettingSpaceColorType)
      );
    }

    //뷰어 툴팁 정보 (텍스트 뷰어일때만 뜸)
    const viewerTooltipIsClosed = window.localStorage.getItem(
      LOCAL_STORAGE_VIEWER_TOOLTIP_IS_CLOSED
    );
    if (viewerTooltipIsClosed || viewerType !== ViewerType.TextViewer) {
      dispatch(setIsViewerToolTipExposed(false));
    } else {
      dispatch(setIsViewerToolTipExposed(true));
    }

    //뷰어 미리보기 댓글 on/off 정보
    const isViewerPreviewCommentOn = window.localStorage.getItem(
      LOCAL_STORAGE_VIEWER_IS_PREVIEW_COMMENT_ON
    );
    if (isViewerPreviewCommentOn)
      dispatch(
        setIsViewerPreviewCommentOn(isViewerPreviewCommentOn === 'true')
      );

    //뷰어 설정 로드 끝
    setIsLoaded(true);
  }, [
    data,
    dispatch,
    isLoaded,
    isMobile,
    isPreview,
    isReverseViewDirection,
    isScrollOnlyImageViewer,
    isSuccess,
    isTapOnlyTalkViewer,
    viewerType,
  ]);

  if (!isLoaded) {
    if (isWebView) {
      return null;
    }
    return (
      <div className="flex-center-full">
        <Loading />
      </div>
    );
  }
  return <>{children}</>;
}
